<template>
  <div
    v-if="emails"
    class="container"
  >
    <v-card-title class="toolbar blue">
      <span class="headline white--text">Email History</span>
      <v-btn
        color="white"
        flat
        icon
        @click="showModal('create')"
      >
        <v-icon>send</v-icon>
      </v-btn>
      <v-spacer />
      <v-text-field
        v-model="search"
        dark
        append-icon="search"
        label="Search"
        single-line
        hide-details
      />
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="emails"
      :rows-per-page-items="rowsPerPageItems"
      :search="search"
      :pagination.sync="pagination"
      :loading="loading"
      class="elevation-1"
      @update:pagination="updatePagination"
    >
      <template
        v-if="!loading"
        slot="items"
        slot-scope="props"
      >
        <td>{{ props.item.id }}</td>
        <td class="text-xs">
          {{ props.item.templateType }}
        </td>
        <td class="text-xs">
          {{ props.item.receiver }}
        </td>
        <td class="text-xs">
          {{ props.item.language }}
        </td>
        <td class="text-xs">
          {{ props.item.createdAt }}
        </td>
        <td class="text-xs-right">
          <router-link
            class="noneDecoration"
            :to="`emails/${props.item.id}/view`"
          >
            <v-btn
              flat
              icon
              color="blue"
            >
              <v-icon>visibility</v-icon>
            </v-btn>
          </router-link>
          <v-btn
            color="blue"
            flat
            icon
            @click="showModal('delete', props.item.id)"
          >
            <v-icon>delete</v-icon>
          </v-btn>
        </td>
      </template>
    </v-data-table>

    <v-dialog
      v-model="createModalOpen"
      width="400"
      persistent
    >
      <v-card>
        <v-card-title
          class="headline grey lighten-2"
          primary-title
        >
          Send Email
        </v-card-title>

        <v-form
          ref="emailForm"
          @submit.prevent="sendEmail"
        >
          <v-card-text>
            <v-text-field
              v-model="userId"
              label="User Id"
            />
            <v-select
              v-model="appClientId"
              :items="appIds"
              label="Select App Client"
            />
            <v-select
              v-if="appClientId"
              v-model="language"
              :items="languages"
              label="Select Language"
            />
            <v-select
              v-if="appClientId"
              v-model="templateType"
              :items="appTemplates"
              label="Select Template"
            />
            <fieldset
              v-if="templateType && dynamicKeys.length"
              class="templateWrapper"
            >
              <legend>Dynamic Data</legend>
              <v-text-field
                v-for="key in dynamicKeys"
                :key="key"
                v-model="data[key]"
                :rules="formRules(key)"
                :label="key"
                required
              />
            </fieldset>
            <div class="mt-3">
              <multi-file-select
                ref="fileSelect"
                name="fileSelect"
                description="Drag your attachment(s) here <br> or click to browse"
                :on-file-change="onFileChange"
              />
            </div>
          </v-card-text>

          <v-divider />

          <v-card-actions>
            <v-spacer />
            <v-btn
              flat
              @click="showModal('create', null, false)"
            >
              Close
            </v-btn>
            <e-button
              flat
              type="submit"
              color="blue"
              text="Send"
              :loading="loading"
            />
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="deleteModalOpen"
      width="400"
      persistent
    >
      <v-card>
        <v-card-title
          class="headline grey lighten-2"
          primary-title
        >
          Delete Template Configuration
        </v-card-title>

        <v-card-text>Do you want to delete this email?</v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            flat
            @click="showModal('delete', null, false)"
          >
            Close
          </v-btn>
          <e-button
            flat
            color="blue"
            :loading="loading"
            text="Delete"
            @click="_deleteEmail"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import _ from 'lodash';
import { formRules, getBase64FileContent, setQuery } from '@/helpers';
import { MultiFileSelect, Button } from '@/components/shared';

export default {
  name: 'Emails',

  components: {
    MultiFileSelect,
    'e-button': Button,
  },

  data() {
    return {
      itemsPerPageArray: [5, 10, 15, 20, 25],
      createModalOpen: false,
      deleteModalOpen: false,
      appIds: [],
      appTemplates: [],
      dynamicKeys: [],
      userId: null,
      appClientId: null,
      templateType: null,
      data: {},
      attachments: [],
      pagination: {
        page: 1,
        rowsPerPage: 5,
        totalItems: 0,
      },
      search: '',
      languages: window.LANGUAGES,
      language: 'EN',
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Type', value: 'type' },
        { text: 'Receiver', value: 'receiver' },
        { text: 'Language', value: 'language' },
        { text: 'Created At', value: 'createdAt' },
        { text: '', value: 'actions' },
      ],
      isLoadTableData: false,
      rowsPerPageItems: [
        { text: '5', value: 5 },
        { text: '10', value: 10 },
        { text: '25', value: 25 },
        { text: 'All', value: -1 },
      ],
    };
  },

  computed: {
    ...mapState({
      emails: state => state.emailStore.emails,
      loading: state => state.emailStore.loading,
      templates: state => state.templateStore.templates,
      templateTypes: state => state.templateTypeStore.templateTypes,
    }),
  },

  watch: {
    loading(newVal, oldVal) {
      if (!newVal && oldVal) {
        this.createModalOpen = false;
        this.deleteModalOpen = false;
        this.$refs.fileSelect.reset();
      }
    },

    templates(val) {
      if (val) {
        this.appIds = _.uniq(val.map(v => v.appClientId));
      }
    },

    appClientId(val) {
      this.filterTemplates(val, this.language);
    },

    language(val) {
      this.filterTemplates(this.appClientId, val);
    },

    templateType(val) {
      const template = _.find(this.templates, { typeId: val });

      this.dynamicKeys = template && template.dynamicKeys
        ? template.dynamicKeys.split(',') : [];
    },

    pagination: {
      handler() {
      },
      deep: true,
    },
  },

  async mounted() {
    this.getTemplates();
    this.getTemplateTypes();
    this.getEmails();
    this.changePage();

    await this.$nextTick();
  },

  methods: {
    ...mapActions([
      'getTemplateTypes',
      'getTemplates',
      'getEmails',
      'putEmail',
      'deleteEmail',
    ]),

    formRules,

    sendEmail() {
      const isValid = this.$refs.emailForm.validate();

      if (isValid) {
        const payload = _.pick(this.$data, ['userId', 'language', 'templateType', 'data', 'attachments']);

        const type = _.find(this.templateTypes, { id: payload.templateType });

        if (type) {
          payload.templateType = type.name;
        }

        this.putEmail(payload);
      }
    },

    _deleteEmail() {
      this.deleteEmail(this.activeId);
      this.activeId = null;
    },

    showModal(type, id, open = true) {
      this.activeId = id;
      this.mode = type;
      this.activeTemplate = {};
      this[`${type}ModalOpen`] = open;
    },

    filterTemplates(appClientId, language) {
      this.appTemplates = this.templates
        ? this.templates
          .filter(v => v.appClientId === appClientId && v.language === language)
          .map((v) => {
            const item = _.find(this.templateTypes, { id: v.typeId });

            return {
              text: item.name,
              value: item.id,
            };
          })
        : [];
    },

    // debounceSearch: _.debounce(function (search) {
    //   this.search = search
    // }, 500),

    async onFileChange(files) {
      try {
        this.attachments = await Promise.all(
          files.map(async (file) => {
            const { content, prefix } = await getBase64FileContent(file);
            const { name, type } = file;
            const ext = name.split('.')[1];

            return {
              name,
              ext,
              type,
              content,
              prefix,
            };
          }),
        );
      } catch (err) {
        console.error(err);
      }
    },
    updatePagination(pagination) {
      if (this.isLoadTableData) {
        setQuery({ page: pagination.page }, this.$route, this.$router);
      }
    },
    changePage() {
      const { page } = this.$route.query;
      this.pagination.page = page ? Number(page) : 1;
    },
  },
};
</script>

<style lang="scss">
.justfifyEnd {
  display: flex;
  justify-content: flex-end;
}
.templateWrapper {
  padding: 15px;
}
.noneDecoration {
  text-decoration: none;
}
.dropbox {
  outline: 2px dashed grey; /* the dash box */
  outline-offset: -10px;
  background: lightcyan;
  color: dimgray;
  padding: 10px 10px;
  min-height: 200px; /* minimum height */
  position: relative;
  cursor: pointer;
}
.inputFile {
  opacity: 0; /* invisible but it's there! */
  width: 100%;
  height: 200px;
  position: absolute;
  cursor: pointer;
}
.dropbox:hover {
  background: lightblue; /* when mouse over to the drop zone, change color */
}
.dropbox p {
  font-size: 1.2em;
  text-align: center;
  padding: 50px 0;
}
</style>



// WEBPACK FOOTER //
// src/views/email/Emails.vue